import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon, SearchIcon, XIcon } from 'lucide-react';
import { articles } from '../data/articles';
import { usePageMeta } from '../hooks/usePageMeta';
const ALL = 'All';

/**
 * Index of every guide article, with search and category filtering.
 *
 * Search matches the title, standfirst, category, and target keyword, so a
 * reader typing "nursing" or "accreditation" finds the article whether or not
 * the word is in its headline.
 */
export function ArticlesPage() {
  usePageMeta('Online Degree Guides & Articles | Online College Guide', 'Browse in-depth guides on accredited online degree programs, admissions, financial aid, and careers. Get matched free by career goals, schedule, and budget.');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ALL);
  const categories = useMemo(() => {
    const unique = Array.from(new Set(articles.map(a => a.category)));
    return [ALL, ...unique.sort()];
  }, []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter(article => {
      if (category !== ALL && article.category !== category) return false;
      if (!q) return true;
      return [article.title, article.intro, article.category, article.targetKeyword, article.eyebrow].join(' ').toLowerCase().includes(q);
    });
  }, [query, category]);
  const isFiltered = query.trim() !== '' || category !== ALL;
  const clearFilters = () => {
    setQuery('');
    setCategory(ALL);
  };
  return <main className="bg-white" data-id="element-992" data-mp="2ioepewlcw1ei">
      {/* Hero */}
      <header className="hero-bg" data-id="element-993" data-mp="2inv4d70esww9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-994" data-mp="2im0qudrgw727">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-995" data-mp="jddixwdb63l7">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-996" data-mp="obf0udyzzwv3">
              <li data-id="element-997" data-mp="1rkriqqu8gluk">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-998" data-mp="1rmb2irf3lf13">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-999" data-mp="1moj8nruw7iu9">/</li>
              <li className="text-white/90" data-id="element-1000" data-mp="20m7scwd84jxq">Articles</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-1001" data-mp="1vmd470ku28gd">
            Guides to Online Degree Programs
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-1002" data-mp="2ahsqukj3322q">
            In-depth, plain-language guides on accreditation, admissions,
            financial aid, and where each degree actually leads. Free to read,
            with no obligation.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-1003" data-mp="2fd83581g0px1">
        {/* Search + filters */}
        <div className="mb-8" data-id="element-1004" data-mp="2objxnl5fk7ur">
          <label htmlFor="article-search" className="sr-only" data-id="element-1005" data-mp="2ocld01thbgia">
            Search articles
          </label>
          <div className="relative max-w-xl" data-id="element-1006" data-mp="37yn8pf0hsd3x">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-1007" data-mp="3cweyo3hktp5h" />
            <input id="article-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search guides — try “nursing” or “accreditation”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-1008" data-mp="2y18sngihdqc5" />
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4" role="group" aria-label="Filter by category" data-id="element-1009" data-mp="2k6y6n6xvca9s">
            {categories.map(name => {
            const isActive = category === name;
            return <button key={name} type="button" onClick={() => setCategory(name)} aria-pressed={isActive} className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-200 ${isActive ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy hover:text-navy'}`} data-id="element-1010" data-mp="2363u9ugxgqkb">
                  {name}
                </button>;
          })}
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-1011" data-mp="1agwn673eqsng">
            <span aria-live="polite" data-id="element-1012" data-mp="15iv4hn9w8vci">
              {results.length} {results.length === 1 ? 'guide' : 'guides'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-1013" data-mp="34uxt1bxdkfx1">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1014" data-mp="38bk7uwhtsfl7" />
                Clear filters
              </button>}
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-1015" data-mp="3m51pgbqybi8l">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-1016" data-mp="3m6w1f0k8w00j">
              No guides match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-1017" data-mp="2tg48cbyh7rhf">
              Try a broader term, or get matched with accredited programs
              directly — it takes about three minutes.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-1018" data-mp="33etsychrbq1d">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-1019" data-mp="dpi0vq186nzv">
                Show all guides
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-1020" data-mp="3k706x2rhx3cu">
                Get Matched Free
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1021" data-mp="36f3ib8p1dbl7" />
              </Link>
            </div>
          </div> : <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1022" data-mp="31h200pzfpgas">
            {results.map(article => <li key={article.slug} className="flex" data-id="element-1023" data-mp="3g67szw7oiaa8">
                <Link to={`/article/${article.slug}`} className="group flex flex-col w-full border border-gray-200 rounded-xl p-6 hover:border-navy hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-1024" data-mp="3bdn67en6wxg7">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-crimson mb-3" data-id="element-1025" data-mp="y6ot722vr9pc">
                    {article.category}
                  </p>
                  <h2 className="text-lg font-bold text-navy leading-snug mb-3 group-hover:underline" data-id="element-1026" data-mp="o9khjw6nuwms">
                    {article.title}
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6 line-clamp-3" data-id="element-1027" data-mp="t6aw2f2thk63">
                    {article.intro}
                  </p>
                  <div className="mt-auto pt-4 border-t border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500" data-id="element-1028" data-mp="1lxkq2dm70v3g">
                    <span className="inline-flex items-center gap-1.5" data-id="element-1029" data-mp="17x09lo3r3bd4">
                      <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1030" data-mp="1bxtz2lwucy84" />
                      {article.updated}
                    </span>
                    <span className="inline-flex items-center gap-1.5" data-id="element-1031" data-mp="l9mxark5t6y">
                      <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1032" data-mp="s7jdggdy964b" />
                      {article.readMinutes} min read
                    </span>
                  </div>
                </Link>
              </li>)}
          </ul>}
      </div>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-1033" data-mp="e89zlnd7uo3i">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-1034" data-mp="e9cojpqzaggs">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-1035" data-mp="1awhe7x3rk282">
            Skip the research — get matched instead
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-1036" data-mp="16yjpevdwni57">
            Answer a few questions about your goals, schedule, and budget, and
            we'll connect you with accredited programs that fit. Free, with no
            obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-1037" data-mp="1umjnkgdjqatt">
            Get Matched Free
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1038" data-mp="2250z0s4zfzpf" />
          </Link>
        </div>
      </section>
    </main>;
}